<template>
  <div>
    <!-- Dark header section -->
    <div v-if="!hideHeader" class="page-header">
      <div class="container">
        <div class="header-content">
          <h1 class="header-title">Portfolio<span class="highlight-text">Intelligence</span></h1>
          <p class="header-subtitle">Efficiently analyze portfolios and generate insightful reports</p>
        </div>
      </div>
    </div>

    <div v-if="totalCount > 0" class="container page-content stk-p-4">
      <!-- Page title and count -->
      <div class="page-title-section">
        <h2 class="count-title">{{ totalCount }} Past Analyses</h2>
        
        <!-- Search input -->
         <!-- todo: nonfunctional, must fix or remove-->
        <div class="search-container">
          <span class="search-icon">
            <Icon icon="fas fa-search" />
          </span>
          <input type="text" placeholder="Search Analyses" class="search-input" />
        </div>
      </div>

      <Callout status="info" iconBefore="fas fa-info" class="stk-mb-4">
        <Stack>
          <div><strong>IMPORTANT NOTES:</strong></div>
          <ul>
            <li>When you "launch" a past report, the data displayed will be as-of the original date you ran the report.</li>
            <li>When you "generate new", you'll load AdvisorCore with the portfolios from the original date, but you'll be prompted to run a new analysis with the latest data.</li>
          </ul>
        </Stack>
      </Callout>

      <!-- Table Section -->
      <o-table 
        :data="tableData"
        :loading="loading"
        :paginated="true"
        :per-page="perPage"
        :current-page="currentPage"
        :total="totalCount"
        :default-sort-direction="sortOrder"
        :default-sort="[sortField, sortOrder]"
        backend-sorting
        backend-pagination
        hoverable
        striped
        @sort="onSort"
        @page-change="onPageChange"
        class="modern-table"
      >
        <o-table-column field="created_date" label="Created Date" >
          <template v-slot="props">
            {{ props.row.created_date }} (UTC)
          </template>
        </o-table-column>

        <o-table-column field="current" label="Current">
          <template v-slot="props">
            {{ props.row.current }}
          </template>
        </o-table-column>

        <o-table-column field="benchmark" label="Benchmark">
          <template v-slot="props">
            {{ props.row.benchmark }}
          </template>
        </o-table-column>

        <o-table-column field="proposed" label="Proposed">
          <template v-slot="props">
            {{ props.row.proposed }}
          </template>
        </o-table-column>

        <o-table-column field="client_risk" label="Client Risk">
          <template v-slot="props">
            {{ props.row.client_risk }}
          </template>
        </o-table-column>

        <o-table-column field="values" label="Client Values" >
          <template v-slot="props">
            {{ props.row.values.join(", ") }}
          </template>
        </o-table-column>

        <o-table-column field="can_launch" label="Launch">
          <template v-slot="props">
            <Button v-if="props.row.can_launch" @click="viewAnalysis(props.row.id)" variant="primary" size="3">
              <span>Launch</span>
              <Icon icon="fas fa-external-link-alt" />
            </Button>
          </template>
        </o-table-column>
        <o-table-column field="generate_new" label="Generate New">
          <template v-slot="props">
            <Button @click="generateNewAnalysis(props.row.currentId, props.row.benchmarkId, props.row.proposedId, props.row.client_risk, props.row.values_pks)" variant="ghost" size="3">
              <span>Generate New</span>
              <Icon icon="fas fa-sync" />
            </Button>
          </template>
        </o-table-column>
      </o-table>
      
      <!-- Pagination info below the table -->
      <Level v-if="totalCount > perPage" class="stk-mt-4">
        <LevelLeft>
          <p>Showing {{ Math.min((currentPage - 1) * perPage + 1, totalCount) }} - {{ Math.min(currentPage * perPage, totalCount) }} of {{ totalCount }} analyses</p>
        </LevelLeft>
      </Level>
    </div>

    <div v-else class="stk-p-4">
      No Past Analyses
    </div>
  </div>
</template>

<script>
import { 
  Stack, 
  Title, 
  Button, 
  Icon, 
  Level, 
  LevelLeft, 
  LevelRight, 
  Callout,
  LoaderWithText
} from '@stakeui';

export default {
  name: "PastAdvisorCoreAnalyses",
  components: {
    Stack,
    Title,
    Button,
    Icon,
    Level,
    LevelLeft,
    LevelRight,
    Callout,
    LoaderWithText
  },
  props: {
    entityExternalId: {
      type: String,
      required: false,
      default: null,
    },
    hideHeader: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  data() {
    return {
      tableData: [], // Stores fetched paginated results
      totalCount: 0, // Stores total number of records
      currentPage: 1, // Current page number
      perPage: 10, // Defines number of items per page
      sortField: "created_date", // Default sort field
      sortOrder: "desc", // Default sort order
      loading: false, // Loading state for API calls
    };
  },
  created() {
    this.fetchPastCheckups(this.currentPage);
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page;
      this.fetchPastCheckups(page);
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.fetchPastCheckups(this.currentPage); // Re-fetch with sorting
    },
    async fetchPastCheckups(page) {
      this.loading = true;
      let url = `/api/v1/portfolio_checkups_list/?page=${page}&page_size=${this.perPage}&sort=${this.sortField}&order=${this.sortOrder}`;

      if (this.entityExternalId) {
        url += `&entity_external_id=${this.entityExternalId}`;
      }

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error("Failed to fetch past portfolio checkups");

        const data = await response.json();
        this.tableData = data.results;
        this.totalCount = data.count; // Ensures pagination works
      } catch (error) {
        console.error("Error fetching past portfolio checkups:", error);
      } finally {
        this.loading = false;
      }
    },
    viewAnalysis(id) {
      const url = `/ui/advisor/checkup/auto-portfolio?latest_checkup=${id}`;
      window.open(url, "_blank"); // Opens in a new tab
    },
    generateNewAnalysis(currentId, benchmarkId, proposedId, client_risk, values_pks) {
      // Create an array to store the URL parameters
      let params = [];
      
      // Conditionally add parameters to the array if they're provided
      if (currentId) params.push(`portfolio=${currentId}`);
      if (benchmarkId) params.push(`benchmark=${benchmarkId}`);
      if (proposedId) params.push(`proposed=${proposedId}`);
      if (client_risk) params.push(`client_risk=${client_risk}`);
      if (values_pks && values_pks.length > 0) params.push(`values=${values_pks.join(",")}`);
      
      // Join the parameters with '&' to form the query string
      const queryString = params.join("&");
      
      // Construct the final URL
      const url = `/ui/advisor/checkup/auto-portfolio?${queryString}`;
      
      // Open the URL in a new tab
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
/* Dark header styling */
.page-header {
  background-color: #1a1a2e;
  color: white;
  padding: 2rem 0;
}

.header-content {
  padding: 0 1rem;
}

.header-title {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.highlight-text {
  color: #6c63ff;
}

.header-subtitle {
  font-size: 1rem;
  font-weight: 400;
  opacity: 0.8;
}

/* Page title and search */
.page-title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.count-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9rem;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

/* Table styling */
.page-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

:deep(.o-table) {
  width: 100%;
  color: #333;
}

:deep(.o-table-wrapper) {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #efefef;
}

:deep(.o-table th) {
  font-weight: 600;
  background-color: #f9f9f9;
  font-size: 0.9rem;
  padding: 1rem 0.75rem;
  color: #555;
  border-bottom: 1px solid #eaeaea;
}

:deep(.o-table td) {
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

:deep(.o-table tr.is-hovered) {
  background-color: #f5f7fa !important;
}

:deep(.o-table tr:last-child td) {
  border-bottom: none;
}

/* Create alternating row colors like in the image */
:deep(.o-table tbody tr:nth-child(odd)) {
  background-color: #ffffff;
}

:deep(.o-table tbody tr:nth-child(even)) {
  background-color: #f9f9f9;
}

/* Pagination styling */
:deep(.o-pagination) {
  margin-top: 1rem;
}
</style>